import React, { useState, useEffect } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { Users, BookOpen, Calendar, DollarSign, TrendingUp, Award } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [teacherPerformance, setTeacherPerformance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, performanceRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/teacher-performance`)
      ]);
      setAnalytics(analyticsRes.data);
      setTeacherPerformance(performanceRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg text-slate-600">Loading analytics...</div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color, subtitle }) => (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      transition={{ type: 'spring', stiffness: 300 }}
    >
      <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-600">{label}</p>
              <p className={`text-3xl font-bold ${color}`}>{value}</p>
              {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
            </div>
            <div className={`w-12 h-12 rounded-xl ${color.replace('text', 'bg').replace('600', '100')} flex items-center justify-center`}>
              <Icon className={`w-6 h-6 ${color}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );


  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
        <p className="text-lg text-slate-600">Platform overview and analytics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Users}
          label="Total Students"
          value={analytics?.total_students || 0}
          color="text-violet-600"
        />
        <StatCard
          icon={BookOpen}
          label="Total Teachers"
          value={analytics?.total_teachers || 0}
          color="text-indigo-600"
        />
        <StatCard
          icon={Calendar}
          label="Total Bookings"
          value={analytics?.total_bookings || 0}
          color="text-amber-600"
        />
        <StatCard
          icon={DollarSign}
          label="Total Revenue"
          value={`₹${analytics?.total_revenue?.toFixed(2) || 0}`}
          color="text-emerald-600"
        />
      </div>

      {/* Teacher Performance Chart */}
      <Card className="border-0 shadow-lg mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-violet-600" />
            Top Teacher Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {teacherPerformance.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={teacherPerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="teacher_name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="completed_bookings" fill="#7C3AED" name="Completed Sessions" radius={[8, 8, 0, 0]} />
                <Bar dataKey="total_earnings" fill="#10B981" name="Earnings (₹)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-slate-600 py-8">No teacher performance data available</p>
          )}
        </CardContent>
      </Card>

      {/* Teacher Performance Table */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-violet-600" />
            Teacher Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="grid grid-cols-5 text-left text-sm font-semibold text-slate-700 px-4">
              <span>Teacher</span>
              <span>Total Bookings</span>
              <span>Completed</span>
              <span>Rating</span>
              <span>Earnings</span>
            </div>
            <div className="divide-y divide-slate-100">
              {teacherPerformance.length === 0 ? (
                <div className="py-6 px-4 text-center text-slate-600">No teacher data available yet.</div>
              ) : (
                teacherPerformance.map((teacher) => (
                  <div key={teacher.teacher_id} className="grid grid-cols-5 items-center py-3 px-4 hover:bg-slate-50">
                    <div className="font-medium">{teacher.teacher_name}</div>
                    <div>{teacher.total_bookings}</div>
                    <div>{teacher.completed_bookings}</div>
                    <div>
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                        ⭐ {teacher.rating.toFixed(1)}
                      </div>
                    </div>
                    <div className="font-semibold text-emerald-600">₹{teacher.total_earnings.toFixed(2)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;